<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<!-- SPEC 42 / P37-20 — Relatório mensal operacional congelado -->
<script setup>
import { computed, onMounted, ref } from 'vue';
import RelatorioAPI from 'dashboard/api/relatorio';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const currentRole = useMapGetter('getCurrentRole');
const isAdmin = computed(() => currentRole.value === 'administrator');

const reports = ref([]);
const selected = ref(null);
const isLoading = ref(false);
const isGenerating = ref(false);

const STATUS_LABELS = {
  ready: 'Pronto',
  generating: 'Gerando',
  failed: 'Falhou',
  skipped: 'Não gerado',
};

const loadReports = async () => {
  isLoading.value = true;
  try {
    const { data } = await RelatorioAPI.relatorioMensal();
    reports.value = data.data || [];
  } catch {
    useAlert('Não foi possível carregar os relatórios mensais.');
  } finally {
    isLoading.value = false;
  }
};

const openReport = async report => {
  try {
    const { data } = await RelatorioAPI.relatorioMensalShow(report.id);
    selected.value = data.data;
  } catch {
    useAlert('Não foi possível abrir o relatório.');
  }
};

const generate = async () => {
  isGenerating.value = true;
  try {
    const { data } = await RelatorioAPI.gerarRelatorioMensal();
    useAlert('Relatório gerado.');
    selected.value = data.data;
    await loadReports();
  } catch {
    useAlert('Não foi possível gerar o relatório.');
  } finally {
    isGenerating.value = false;
  }
};

const payload = computed(() => selected.value?.payload || {});
const channelRows = computed(() =>
  Array.isArray(payload.value.canais) ? payload.value.canais : []
);
const hasCrmMetrics = computed(
  () =>
    payload.value.crm &&
    (payload.value.crm.synced != null || payload.value.crm.failed != null)
);
const csatDistributionRows = computed(() => {
  const distribution = payload.value.satisfacao?.distribuicao || {};
  const total = Number(payload.value.satisfacao?.respostas || 0);

  return [1, 2, 3, 4, 5].map(score => {
    const count = Number(distribution[String(score)] || 0);
    return {
      score,
      count,
      percent: total > 0 ? Math.round((count / total) * 100) : 0,
    };
  });
});
const hasCsatDistribution = computed(
  () =>
    Boolean(payload.value.satisfacao?.respostas) &&
    csatDistributionRows.value.some(row => row.count > 0)
);

onMounted(loadReports);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
  <div class="grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
    <LaisCard title="Relatório mensal">
      <div class="mb-3 flex items-center justify-between">
        <p class="text-[12px] text-lais-text-muted">
          Gerado no dia 1 para contas com 10+ leads no mês anterior.
        </p>
        <LaisPrimaryButton
          v-if="isAdmin"
          size="sm"
          icon="i-lucide-refresh-cw"
          :is-loading="isGenerating"
          @click="generate"
        >
          Gerar mês anterior
        </LaisPrimaryButton>
      </div>

      <div v-if="isLoading" class="py-10 text-center text-lais-text-muted">
        Carregando...
      </div>
      <div
        v-else-if="!reports.length"
        class="py-10 text-center text-[13px] text-lais-text-muted"
      >
        Nenhum relatório mensal disponível ainda.
      </div>
      <div v-else class="grid gap-2">
        <button
          v-for="report in reports"
          :key="report.id"
          type="button"
          class="flex items-center justify-between rounded-lais border border-lais-border-soft px-3 py-2 text-left hover:border-lais-primary"
          :class="{
            'border-lais-primary': selected && selected.id === report.id,
          }"
          @click="openReport(report)"
        >
          <div>
            <p class="text-[14px] font-semibold text-lais-text">
              {{ report.period_label }}
            </p>
            <p
              v-if="report.skipped_reason === 'less_than_10_leads'"
              class="text-[12px] text-lais-text-muted"
            >
              Mês anterior teve menos de 10 leads.
            </p>
          </div>
          <span
            class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
            :class="
              report.status === 'failed'
                ? 'bg-rose-600/15 text-rose-700'
                : 'bg-lais-primary/10 text-lais-primary'
            "
          >
            {{ STATUS_LABELS[report.status] || report.status }}
          </span>
        </button>
      </div>
    </LaisCard>

    <LaisCard v-if="selected" :title="`Detalhe — ${selected.period_label}`">
      <p
        v-if="selected.status === 'failed'"
        class="mb-3 rounded-lais bg-rose-50 px-3 py-2 text-[13px] text-rose-700"
      >
        {{ selected.error_message || 'Falha na geração.' }}
        <LaisPrimaryButton
          v-if="isAdmin"
          class="mt-2"
          size="sm"
          @click="generate"
        >
          Gerar novamente
        </LaisPrimaryButton>
      </p>
      <p class="mb-4 text-[13px] leading-6 text-lais-text-body">
        {{ selected.summary || 'Sem resumo.' }}
      </p>
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div class="rounded-lais bg-lais-bg-soft px-3 py-2">
          <p class="text-[11px] text-lais-text-muted">Atendimentos</p>
          <p class="text-[16px] font-semibold">
            {{ payload.atendimentos?.total ?? '—' }}
          </p>
        </div>
        <div class="rounded-lais bg-lais-bg-soft px-3 py-2">
          <p class="text-[11px] text-lais-text-muted">Leads</p>
          <p class="text-[16px] font-semibold">
            {{ payload.leads?.total ?? '—' }}
          </p>
        </div>
        <div class="rounded-lais bg-lais-bg-soft px-3 py-2">
          <p class="text-[11px] text-lais-text-muted">Qualificados</p>
          <p class="text-[16px] font-semibold">
            {{ payload.leads?.qualificados ?? '—' }}
          </p>
        </div>
        <div class="rounded-lais bg-lais-bg-soft px-3 py-2">
          <p class="text-[11px] text-lais-text-muted">Visitas</p>
          <p class="text-[16px] font-semibold">
            {{ payload.conversoes?.visitas ?? '—' }}
          </p>
        </div>
        <div class="rounded-lais bg-lais-bg-soft px-3 py-2">
          <p class="text-[11px] text-lais-text-muted">Fechamentos</p>
          <p class="text-[16px] font-semibold">
            {{ payload.conversoes?.fechamentos ?? '—' }}
          </p>
        </div>
        <div
          v-if="payload.satisfacao?.respostas"
          class="rounded-lais bg-lais-bg-soft px-3 py-2"
        >
          <p class="text-[11px] text-lais-text-muted">CSAT</p>
          <p class="text-[16px] font-semibold">
            {{ payload.satisfacao?.csat_media ?? '—' }}
          </p>
        </div>
        <div
          v-if="hasCrmMetrics"
          class="rounded-lais bg-lais-bg-soft px-3 py-2"
        >
          <p class="text-[11px] text-lais-text-muted">CRM sincronizado</p>
          <p class="text-[16px] font-semibold">
            {{ payload.crm?.synced ?? '—' }}
          </p>
        </div>
        <div
          v-if="hasCrmMetrics"
          class="rounded-lais bg-lais-bg-soft px-3 py-2"
        >
          <p class="text-[11px] text-lais-text-muted">CRM com falha</p>
          <p class="text-[16px] font-semibold">
            {{ payload.crm?.failed ?? '—' }}
          </p>
        </div>
      </div>

      <div v-if="channelRows.length" class="mt-4 grid gap-2">
        <h3 class="text-[12px] font-semibold uppercase text-lais-text-muted">
          Desempenho por canal
        </h3>
        <div class="grid gap-2">
          <div
            v-for="channel in channelRows"
            :key="channel.canal"
            class="grid gap-2 rounded-lais bg-lais-bg-soft px-3 py-2 sm:grid-cols-[1fr_auto]"
          >
            <p class="text-[14px] font-semibold text-lais-text">
              {{ channel.canal || 'desconhecido' }}
            </p>
            <dl class="grid grid-cols-2 gap-3 text-right text-[12px]">
              <div>
                <dt class="text-lais-text-muted">Leads</dt>
                <dd class="font-semibold text-lais-text">
                  {{ channel.leads ?? '—' }}
                </dd>
              </div>
              <div>
                <dt class="text-lais-text-muted">Qualificados</dt>
                <dd class="font-semibold text-lais-text">
                  {{ channel.qualificados ?? '—' }}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <div v-if="hasCsatDistribution" class="mt-4 grid gap-2">
        <h3 class="text-[12px] font-semibold uppercase text-lais-text-muted">
          Distribuição CSAT
        </h3>
        <div class="grid gap-1.5 rounded-lais bg-lais-bg-soft px-3 py-3">
          <div
            v-for="row in csatDistributionRows"
            :key="row.score"
            class="grid grid-cols-[44px_1fr_32px] items-center gap-2 text-[12px]"
          >
            <span class="font-semibold text-lais-text">
              Nota {{ row.score }}
            </span>
            <span class="h-2 overflow-hidden rounded-full bg-white">
              <span
                class="block h-full rounded-full bg-lais-primary"
                :style="{ width: `${row.percent}%` }"
              />
            </span>
            <span class="text-right font-semibold text-lais-text">
              {{ row.count }}
            </span>
          </div>
        </div>
      </div>
    </LaisCard>
  </div>
</template>
